// @systeme: interface
// Les foyers : usage du mois, coût de l'IA sur trente jours, marge. Un foyer déficitaire
// est signalé sobrement. L'essai se prolonge au cas par cas. Aucun contenu.
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { FoyerAdmin } from '@foyer/shared';
import { Chargement } from '../../composants/Chargement';
import { useMessages } from '../../composants/Messages';
import { api } from '../../lib/api';
import { formaterDateComplete, formaterJourCourt, jourDe } from '../../lib/dates';

const STATUTS: Record<FoyerAdmin['statut'], string> = {
  essai: 'à l’essai',
  actif: 'abonné',
  impaye: 'impayé',
  resilie: 'résilié',
  expire: 'essai terminé',
};

function CarteFoyer({ foyer, onMaj }: { foyer: FoyerAdmin; onMaj: (f: FoyerAdmin) => void }) {
  const { dire, direErreur } = useMessages();
  const [essai, setEssai] = useState(foyer.essaiFinLe ?? '');
  const [ouvert, setOuvert] = useState(false);

  async function prolonger() {
    try {
      onMaj(await api.admin.prolongerEssai(foyer.id, essai));
      setOuvert(false);
      dire('C’est réglé.');
    } catch (e) {
      direErreur(e);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontWeight: 600 }}>{foyer.nom}</Typography>
        <Typography variant="body2" color="text.secondary">
          {foyer.membres} membre{foyer.membres > 1 ? 's' : ''} · {foyer.planCle} ·{' '}
          {STATUTS[foyer.statut]}
          {foyer.prixTtc !== null ? ` · ${foyer.prixTtc.toFixed(2).replace('.', ',')} €` : ''}
          {foyer.essaiFinLe ? ` · essai jusqu’au ${formaterJourCourt(foyer.essaiFinLe)}` : ''}
        </Typography>
        {foyer.deficitaire && (
          <Typography variant="body2" sx={{ color: 'secondary.main', fontWeight: 600 }}>
            coûte plus qu’il ne paie
          </Typography>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Ce mois : {foyer.vocauxMois} vocaux, {foyer.photosMois} photos · IA sur trente jours :{' '}
        {foyer.coutIaTrenteJours.toFixed(2).replace('.', ',')} € · depuis le{' '}
        {formaterDateComplete(jourDe(foyer.creeLe))}
      </Typography>
      {ouvert ? (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
          <TextField
            type="date"
            size="small"
            value={essai}
            onChange={(e) => setEssai(e.target.value)}
            inputProps={{ 'aria-label': `Fin d’essai de ${foyer.nom}` }}
          />
          <Button
            size="small"
            variant="contained"
            disabled={!essai}
            onClick={() => void prolonger()}
          >
            Enregistrer
          </Button>
          <Button size="small" color="inherit" onClick={() => setOuvert(false)}>
            Annuler
          </Button>
        </Stack>
      ) : (
        <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => setOuvert(true)}>
          Prolonger l’essai
        </Button>
      )}
    </Paper>
  );
}

export function Foyers() {
  const { direErreur } = useMessages();
  const [foyers, setFoyers] = useState<FoyerAdmin[] | undefined>();

  useEffect(() => {
    api.admin.foyers().then(setFoyers).catch(direErreur);
  }, [direErreur]);

  if (!foyers) return <Chargement />;
  return (
    <Stack spacing={1.5}>
      <Typography color="text.secondary">
        Des chiffres, jamais un contenu. Un foyer « coûte plus qu’il ne paie » quand l’IA lui a
        coûté plus sur trente jours que son abonnement mensuel.
      </Typography>
      {foyers.map((f) => (
        <CarteFoyer
          key={f.id}
          foyer={f}
          onMaj={(maj) => setFoyers((liste) => liste?.map((x) => (x.id === maj.id ? maj : x)))}
        />
      ))}
    </Stack>
  );
}
